import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useFetchClass, useFetchClassWaitlistEntry, useListClientServices, useListVisits } from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import Auth from 'screens/Account/Auth';
import { ClassRoomLayout, ClassRoomLayoutLoadingPlaceholder } from 'screens/Class/components/ClassRoomLayout';
import { Services } from 'screens/Prices/components/Services';
import { PickaspotReservation } from 'types/class';
import { ClientVisit } from 'types/client';
import { ApiClientException } from 'utils/apiClientException';
import { capitalizeFirstLetter, classHasCapacity, isClassBookable } from 'utils/functions';
import { HTTP } from 'utils/http';
import * as Sentry from '@sentry/react';
import { MindbodyClass } from 'types/class';

export interface ReservationFormValues {
  SpotNumber?: number;
  serviceId?: number;
  classId: string;
  Waitlist?: boolean | number;
  isCommunityClass?: boolean;
  isEnrollment?: boolean;
}

const buttonBorderRadius = `${mondayMindbody?.customization?.buttonBorderRadius}px`;

export function ClassScreen() {
  const queryClient = useQueryClient();

  const [selectedService, setSelectedService] = useState<number>();
  const [isLoadingServiceConfirmation, setIsLoadingServiceConfirmation] = useState(false);

  const [hasAlreadyUsedRoutineService, setHasAlreadyUsedRoutineService] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const isEnrollmentInURL = location.pathname.includes('enrollment');
  const { isLoggedIn, user } = useUserContext();
  const toast = useToast();
  const formMethods = useForm<ReservationFormValues>({});
  const { formState, handleSubmit, resetField } = formMethods;

  const { refetch: refetchVisits } = useListVisits() || {};
  const { data: servicesData, refetch: refetchClientServices } = useListClientServices();
  const { data: visits } = useListVisits() || {};
  const {
    data: mindbodyClass,
    error,
    isLoading: classLoading,
    refetch: refetchClass,
  } = useFetchClass(id, isEnrollmentInURL);
  const { data: waitlistEntry, refetch: refetchWaitlist } = useFetchClassWaitlistEntry(id);

  const clientServices = servicesData ? Object.values(servicesData) : [];
  const noRoutineServices = servicesData
    ? Object.values(servicesData).filter((service) => !service.Name.toLowerCase().includes('challenge'))
    : [];

  // Détecter si la classe n'a pas de plan de salle
  const hasNoRoomLayout = !mindbodyClass?.PickASpot;

  const visitInformations: ClientVisit | undefined = useMemo(() => {
    if (!visits) return undefined;
    return visits.find((visit) => visit.ClassId === Number(id));
  }, [visits, id]);

  useEffect(() => {
    if (visitInformations && visitInformations.Service.Name.toLowerCase().includes('challenge')) {
      setHasAlreadyUsedRoutineService(true);
    }
  }, [visitInformations]);

  const selectedSpot = formMethods.watch('SpotNumber');
  const primaryColor = mondayMindbody?.customization?.color ?? '#ff7f00';

  useEffect(() => {
    if (mindbodyClass) {
      if (!isClassBookable(mindbodyClass) && !isEnrollmentInURL && !hasNoRoomLayout) {
        toast({
          title: t("Ce cours n'est pas réservable"),
          status: 'error',
        });
      }
    }
  }, [mindbodyClass, toast, t, isEnrollmentInURL]);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  async function bookClass(body: Partial<ReservationFormValues> = {}) {
    Object.assign(body, { classId: id });
    body.isEnrollment = isEnrollmentInURL;

    try {
      await HTTP.post<PickaspotReservation>('/class/book', body).send();
      resetField('SpotNumber');
      setShowSuccessMessage(true);
      await Promise.all([refetchVisits(), refetchClientServices(), refetchClass(), refetchWaitlist()]);
      if (
        body.serviceId === 49537 || // 113072
        body.serviceId === 51802 || // 113072
        body.serviceId === 21630 || // 113081
        body.serviceId === 12867 // 113077
      ) {
        setHasAlreadyUsedRoutineService(true);
      }
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag('where', 'ClassScreen:bookClass');
        scope.setExtra('classId', id);
        scope.setExtra('body', body);
        Sentry.captureException(error);
      });

      if (error instanceof ApiClientException) {
        let title = error.getMessage();

        // Check if the error is related to full waitlist
        if (
          error.Message.includes('ClassSignUpsFull') ||
          error.Message.includes('Registration for the class is full')
        ) {
          title = t("La liste d'attente est complète");
        }

        toast({
          title,
          status: 'error',
        });

        refetchVisits();
        await Promise.all([refetchClass(), refetchWaitlist()]);
      } else {
        console.error(error);
      }
    }
  }

  const onSubmit = async () => {
    try {
      const { data } = await refetchClientServices();
      if (!data) {
        // Capture the exception with Sentry
        const error = new Error('Data is undefined');
        Sentry.withScope((scope) => {
          scope.setTag('where', 'ClassScreen:onSubmit');
          scope.setExtra('context', 'Fetching client services');
          scope.setExtra('data', data);
          Sentry.captureException(error);
        });
        throw error;
      }
      const [service] = data;

      if (isCommunityClass) {
        await bookClass({ isCommunityClass: true }); // Appeler bookClass sans arguments pour réserver directement
        return;
      }

      if (classIsFull) {
        if (waitlistEntry) {
          await HTTP.delete(`/class/${id}/waitlist`).send();
          return queryClient.setQueryData(['/class', id, '/waitlist'], null);
        }

        const isWaitlist = true;
        if (data?.length === 1) {
          // Premier breadcrumb pour service unique
          Sentry.captureMessage('Single service available, confirming selection', {
            level: 'info',
            tags: {
              category: 'service-selection',
            },
            extra: {
              isWaitlist: isWaitlist,
              serviceId: service.Id,
              user: `${user?.FirstName} ${user?.LastName}`,
              email: user?.Email,
            },
          });
          confirmServiceSelection(service.Id, isWaitlist);
        } else {
          // Deuxième breadcrumb pour services multiples
          Sentry.captureMessage('Multiple services available, opening selection modal', {
            level: 'info',
            tags: {
              category: 'service-selection',
            },
            extra: {
              isWaitlist: isWaitlist,
              user: `${user?.FirstName} ${user?.LastName}`,
              email: user?.Email,
            },
          });
          onOpen();
        }
      }

      if (isEnrollmentInURL || hasNoRoomLayout) {
        return onOpen();
      }

      if (!selectedSpot) return;

      if (!isCommunityClass && data?.length === 1) {
        confirmServiceSelection(service.Id); // If only one service
      } else {
        onOpen();
      }
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag('where', 'ClassScreen:onSubmit');
        scope.setExtra('selectedSpot', selectedSpot);
        scope.setExtra('isEnrollmentInURL', isEnrollmentInURL);
        Sentry.captureException(e);
      });

      console.error(e);
      return null;
    }
  };

  const confirmServiceSelection = async (serviceId?: number, isWaitlist?: boolean) => {
    if (classIsFull && isWaitlist === undefined) isWaitlist = true;

    // Troisième breadcrumb pour tentative de sélection de service
    Sentry.captureMessage('Attempting to confirm service selection', {
      level: 'info',
      tags: {
        category: 'service-selection',
      },
      extra: {
        classId: id,
        selectedService: selectedService,
        serviceId: serviceId,
        isEnrollmentInURL: isEnrollmentInURL,
        isWaitlist: isWaitlist,
        selectedSpot: selectedSpot,
        user: `${user?.FirstName} ${user?.LastName}`,
        email: user?.Email,
      },
    });

    if (
      !id ||
      (!selectedService && !serviceId) ||
      (!isEnrollmentInURL && !hasNoRoomLayout && !isWaitlist && !selectedSpot)
    ) {
      // Quatrième breadcrumb pour paramètres manquants
      Sentry.captureMessage('Exiting confirmServiceSelection due to missing parameters', {
        level: 'warning',
        tags: {
          category: 'service-selection',
        },
        extra: {
          classId: id,
          selectedService: selectedService,
          serviceId: serviceId,
          isEnrollmentInURL: isEnrollmentInURL,
          isWaitlist: isWaitlist,
          selectedSpot: selectedSpot,
          user: `${user?.FirstName} ${user?.LastName}`,
          email: user?.Email,
        },
      });
      return;
    }

    setIsLoadingServiceConfirmation(true);

    const body: ReservationFormValues = {
      SpotNumber: isEnrollmentInURL || isWaitlist || hasNoRoomLayout ? undefined : selectedSpot, // Allow undefined SpotNumber if it's an enrollment, waitlist, or has no room layout
      classId: id,
      serviceId: selectedService ?? serviceId,
      Waitlist: isWaitlist ? true : undefined,
    };

    if (waitlistEntry) body.Waitlist = waitlistEntry.Id;

    // Book class
    await bookClass(body);
    setIsLoadingServiceConfirmation(false);
    onClose();
  };

  const isBooked =
    isEnrollmentInURL || hasNoRoomLayout
      ? visits?.some(
          (visit) =>
            visit.ClassId === mindbodyClass?.Id && visit.AppointmentStatus === 'Booked' && visit.ClientId === user?.Id,
        )
      : !!mindbodyClass?.PickASpot?.reservations.find((reservation) => reservation.memberExternalId === user?.Id);

  /* Not logged in */
  if (!isLoggedIn)
    return (
      <Stack>
        <Heading>{t('Connectez vous pour réserver')}</Heading>
        <Auth />;
      </Stack>
    );

  /* Loading */
  const isLoading = classLoading;
  if (isLoading) return <ClassLoading />;

  /* Required service not found*/
  if (error && error.ErrorCode === 'ServicePurchaseRequired')
    return (
      <Stack>
        <Heading>{t('Recharger votre compte')}</Heading>
        <Services classId={id} />
      </Stack>
    );

  if (!mindbodyClass || !id) return null;
  const classIsFull = !classHasCapacity(mindbodyClass);

  const isCommunityClass = /communauté|communaute/i.test(mindbodyClass?.ClassDescription?.Name ?? '');

  const isWaitlistFull = (mindbodyClass: MindbodyClass) => {
    return mindbodyClass.TotalBookedWaitlist >= mindbodyClass.WaitListSize;
  };

  function getSubmitButtonLabel() {
    if (waitlistEntry && classIsFull) return t("Quitter la file d'attente");
    if (waitlistEntry) return t('Confirmer ma réservation');
    if (isBooked) return t('Réserver un autre spot');
    if (classIsFull) return t("Ajoutez-moi à la liste d'attente");
    return t('Valider ma réservation');
  }
  /* Reservation form */
  return (
    <Stack>
      <Center>
        <Button mt={4} variant="transparent" className="backButton" type="submit" onClick={() => navigate('/')}>
          {t('Retour')}
        </Button>
      </Center>
      <Stack alignItems="center">
        <Text className="classDate" fontSize="xl" fontWeight="semibold" textTransform="capitalize" color="black">
          {format(new Date(mindbodyClass.StartDateTime), "iiii d MMMM ' - ' HH:mm", { locale: fr })}
        </Text>
        <Text fontSize="xl" fontWeight="semibold" color={primaryColor} className="classDescriptionName">
          {mindbodyClass.ClassDescription.Name.toLowerCase()} avec{' '}
          {capitalizeFirstLetter(mindbodyClass.Staff.FirstName)}
        </Text>
        <Text
          fontSize="normal"
          textTransform="uppercase"
          fontWeight="medium"
          color="black"
          className="classDescriptionName"
          mb={2}
        >
          studio {mindbodyClass?.Location.Name}
        </Text>
      </Stack>

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ClassRoomLayout classId={id} isEnrollmentInURL={isEnrollmentInURL} />
          {mondayMindbody.siteId === '5736526' && (
            <Text
              className="classNotice"
              color="gray.400"
              fontSize="normal"
              fontWeight="normal"
              textAlign="center"
              mt={5}
              mb={1}
              display={isEnrollmentInURL === true ? 'none' : 'block'}
            >
              Le premier rang est recommandé aux riders réguliers. C'est vous qui fixerez le rythme pendant la session.
            </Text>
          )}
          <ButtonGroup w="full" justifyContent="center" flexDirection={{ base: 'column', md: 'row' }}>
            {(!isCommunityClass || (isCommunityClass && !isBooked)) && !(isBooked && isEnrollmentInURL) && (
              <Button
                isDisabled={
                  (!selectedSpot && !classIsFull && !isEnrollmentInURL && !hasNoRoomLayout) ||
                  (classIsFull && isWaitlistFull(mindbodyClass) && !waitlistEntry)
                }
                mt={4}
                variant="contained"
                className="bookButton"
                type="submit"
                isLoading={formState.isSubmitting}
              >
                {isLoadingServiceConfirmation ? <Spinner color="white" size="sm" /> : getSubmitButtonLabel()}
              </Button>
            )}

            {isBooked && (
              <Button
                mt={4}
                className="seeAllBookings"
                variant="transparent"
                type="submit"
                onClick={() => navigate('/account/visits')}
              >
                {t('Voir toutes mes réservations')}
              </Button>
            )}
          </ButtonGroup>
          {showSuccessMessage && (
            <Text
              className="bookingSuccessMessage"
              color="green.500"
              fontSize="lg"
              fontWeight="bold"
              textAlign="center"
              mt={4}
            >
              Votre réservation a bien été prise en compte
            </Text>
          )}
          {(isEnrollmentInURL || hasNoRoomLayout) && (
            <Box mt={4} className="enrollmentNotice" maxW="600px" mx="auto">
              <Text align="center" fontSize="sm">
                Lors de la réservation à cette séance, vous acceptez également de recevoir des e-mails promotionnels de
                notre partenaire lululemon sur les produits, services, offres événements conformément à leur{' '}
                <Link
                  target="_blank"
                  sx={{ textDecoration: 'underline' }}
                  href=" https://www.lululemon.fr/fr-fr/legal/politique-de-confidentialite/privacy.html"
                >
                  politique de confidentialité
                </Link>{' '}
                - vous pouvez vous désabonner via lululemon à tout moment.
              </Text>
            </Box>
          )}
        </form>
      </FormProvider>
      {/* Service Selection Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent className="modalServicesChoice">
          <ModalHeader>Crédits à utiliser</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Select placeholder="Choisir des crédits" onChange={(e) => setSelectedService(parseInt(e.target.value))}>
              {!hasAlreadyUsedRoutineService &&
                clientServices?.map((service) => (
                  <option key={service.Id} value={service.Id}>
                    {service.Name}
                  </option>
                ))}
              {hasAlreadyUsedRoutineService &&
                noRoutineServices?.map((service) => (
                  <option key={service.Id} value={service.Id}>
                    {service.Name}
                  </option>
                ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="ghost" onClick={onClose} textTransform={'uppercase'}>
                Fermer
              </Button>
              <Button
                borderRadius={buttonBorderRadius}
                textTransform={'uppercase'}
                minWidth="120px"
                colorScheme="primary"
                onClick={() => confirmServiceSelection()}
              >
                {isLoadingServiceConfirmation ? <Spinner color="white" size="sm" /> : t('Confirmer')}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
}

function ClassLoading() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Stack>
      <Center>
        <Button mt={4} variant="transparent" className="backButton" type="submit" onClick={() => navigate('/')}>
          {t('Retour')}
        </Button>
      </Center>
      <Stack alignItems="center">
        <Skeleton w={150} h="40px" />
        <Skeleton w={120} h="30px" />
        <Skeleton w={100} h="45px" />
      </Stack>

      <Center>
        <ClassRoomLayoutLoadingPlaceholder />
      </Center>
      <Center w="full">
        <Skeleton w={200} h="40px" />
      </Center>
    </Stack>
  );
}
